import React from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EmptyState from './EmptyState';

/**
 * DataTable - one dense table for the admin and agent areas (design.md §5.4).
 *
 * Gives every table the things the audit found missing on most of them: small
 * density, a sticky header, right-aligned numeric columns, row hover, a single
 * pagination control, and a real empty state instead of a bare "No X found".
 * Under `sm` it can render each row as a card, so wide tables stop forcing a
 * sideways scroll on phones.
 *
 * Columns:
 *   { id, label, align?, numeric?, width?, hideBelow?, sortable?, render?(row) }
 *
 * Props:
 *  - columns, rows   : the data
 *  - getRowId        : row => key; defaults to row.id
 *  - onRowClick      : makes rows activatable
 *  - loading         : shows a spinner in the body, keeping the header in place
 *  - empty           : { title, description, icon, action } for EmptyState
 *  - pagination      : { page, rowsPerPage, count, onPageChange, onRowsPerPageChange, rowsPerPageOptions }
 *  - sort            : { by, direction, onChange(columnId) }
 *  - mobileCard      : row => node; when given, replaces the table under `sm`
 *  - toolbar         : node rendered above the table (search, filters)
 *  - maxHeight       : caps the scroll area; the sticky header needs one
 */
const DataTable = ({
  columns = [],
  rows = [],
  getRowId,
  onRowClick,
  loading = false,
  empty,
  pagination,
  sort,
  mobileCard,
  toolbar,
  maxHeight = 640,
  size = 'small',
  sx,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { slate } = theme.palette.modern;

  const rowKey = (row, index) => (getRowId ? getRowId(row) : row?.id ?? index);
  const visibleColumns = columns.filter(
    (column) => !(column.hideBelow && isMobile && column.hideBelow === 'sm'),
  );

  const emptyBlock = empty ? (
    <EmptyState dense {...empty} />
  ) : (
    <EmptyState dense title="Nothing to show yet" />
  );

  if (mobileCard && isMobile) {
    return (
      <Box sx={sx}>
        {toolbar}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          emptyBlock
        ) : (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {rows.map((row, index) => (
              <Box key={rowKey(row, index)}>{mobileCard(row)}</Box>
            ))}
          </Box>
        )}
        {pagination && <TablePagination component="div" {...paginationProps(pagination)} />}
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', ...sx }}>
      {toolbar}
      <TableContainer sx={{ maxHeight, overflowX: 'auto' }}>
        <Table stickyHeader size={size}>
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || (column.numeric ? 'right' : 'left')}
                  sx={{ fontWeight: 700, width: column.width, whiteSpace: 'nowrap' }}
                  sortDirection={sort?.by === column.id ? sort.direction : false}
                >
                  {column.sortable && sort ? (
                    <TableSortLabel
                      active={sort.by === column.id}
                      direction={sort.by === column.id ? sort.direction : 'asc'}
                      onClick={() => sort.onChange(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} sx={{ borderBottom: 0 }}>
                  {emptyBlock}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow
                  key={rowKey(row, index)}
                  hover
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:hover': { backgroundColor: slate[50] },
                  }}
                >
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align || (column.numeric ? 'right' : 'left')}
                      sx={column.numeric ? { fontVariantNumeric: 'tabular-nums' } : undefined}
                    >
                      {column.render ? column.render(row) : row?.[column.id]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && <TablePagination component="div" {...paginationProps(pagination)} />}
    </Paper>
  );
};

/** One rowsPerPage scale everywhere, unless a caller overrides it. */
const paginationProps = ({
  page = 0,
  rowsPerPage = 25,
  count = 0,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100],
}) => ({
  page,
  rowsPerPage,
  count,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions,
});

export default DataTable;
